import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Plus,
  RotateCcw,
  Search,
  Trash2,
  Users,
  X,
} from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import {
  Button,
  EmptyState,
  PageHeader,
  StageBadge,
  TableSkeleton,
  cx,
} from "../components/ui";
import { downloadBlob } from "../lib/download";
import { formatMoney, formatRelative, plural } from "../lib/format";
import type { Contact } from "../api/types";

const STAGES = ["lead", "prospect", "active", "inactive", "lost", "vendor"] as const;

/**
 * The contacts table.
 *
 * Filter and page state live in the URL rather than in component state, so a
 * filtered view can be linked to, survives a refresh, and works with the back
 * button. Cursor pagination cannot produce "page 3 of 9", so the control is
 * previous/next rather than numbered -- see `apps/api/pagination.py`.
 */
export function ContactsPage({ showTrash = false }: { showTrash?: boolean }) {
  const { user } = useAuth();
  const label = user?.account?.primary_object_label ?? "Contacts";
  const queryClient = useQueryClient();

  const [params, setParams] = useSearchParams();
  const search = params.get("search") ?? "";
  const stage = params.get("stage") ?? "";
  const tags = params.get("tags") ?? "";
  const cursor = params.get("cursor") ?? "";

  // The input is local so typing stays responsive; the URL updates on a debounce
  // so we are not firing a request per keystroke.
  const [draft, setDraft] = useState(search);
  const debounceTimer = useRef<number | undefined>(undefined);

  useEffect(() => setDraft(search), [search]);
  useEffect(() => () => window.clearTimeout(debounceTimer.current), []);

  function onDraftChange(value: string) {
    setDraft(value);
    window.clearTimeout(debounceTimer.current);
    debounceTimer.current = window.setTimeout(() => {
      update({ search: value, cursor: "" });
    }, 250);
  }

  function update(next: Record<string, string>) {
    setParams(
      (prev) => {
        const merged = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(next)) {
          if (value) merged.set(key, value);
          else merged.delete(key);
        }
        return merged;
      },
      { replace: true },
    );
  }

  const query = useQuery({
    queryKey: [showTrash ? "trash" : "contacts", search, stage, tags, cursor],
    queryFn: ({ signal }) =>
      showTrash
        ? api.trash(signal)
        : api.contacts(
            {
              search: search || undefined,
              lifecycle_stage: stage || undefined,
              tags: tags || undefined,
              cursor: cursor || undefined,
              page_size: 25,
            },
            signal,
          ),
    placeholderData: (prev) => prev,
  });

  const contacts = query.data?.results ?? [];
  const nextCursor = cursorFrom(query.data?.next);
  const filtering = Boolean(search || stage || tags);
  const canWrite = (user?.permissions ?? []).includes("contacts.write");

  // Tags present in the account, fetched independently of the current filter.
  //
  // Deriving them from the rows on screen looks cheaper and is a trap: applying a
  // tag filter narrows those rows, the facet list narrows with them, and the
  // other tags disappear. You could filter by one tag and then be unable to
  // switch to another without clearing first.
  const facets = useQuery({
    queryKey: ["contact-facets"],
    queryFn: ({ signal }) => api.contacts({ page_size: 100 }, signal),
    staleTime: 5 * 60_000,
  });

  const availableTags = useMemo(() => {
    const seen = new Set<string>();
    for (const contact of facets.data?.results ?? []) {
      for (const tag of (contact.tags as string[] | undefined) ?? []) seen.add(tag);
    }
    return [...seen].sort();
  }, [facets.data]);

  const selectedTags = tags.split(",").map((t) => t.trim()).filter(Boolean);

  const exportCsv = useMutation({
    mutationFn: () =>
      api.exportContactsCsv({
        search: search || undefined,
        lifecycle_stage: stage || undefined,
        tags: tags || undefined,
      }),
    onSuccess: (blob) =>
      downloadBlob(blob, `contacts-${new Date().toISOString().slice(0, 10)}.csv`),
  });

  const restore = useMutation({
    mutationFn: (id: string) => api.restoreContact(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["trash"] });
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
      void queryClient.invalidateQueries({ queryKey: ["usage"] });
    },
  });

  if (showTrash) {
    return (
      <>
        <PageHeader
          title="Bin"
          subtitle="Deleted records, kept for 30 days."
          actions={
            <Link
              to="/customers/contacts"
              className="text-sm text-slate-600 underline hover:text-slate-900"
            >
              Back to {plural(label).toLowerCase()}
            </Link>
          }
        />
        <TrashTable
          contacts={contacts}
          pending={query.isPending}
          error={query.isError ? query.error : null}
          onRestore={(id) => restore.mutate(id)}
          restoring={restore.isPending}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={label}
        subtitle={
          query.isSuccess
            ? `${contacts.length} shown${query.data?.next ? ", more available" : ""}`
            : `Everyone who buys from you`
        }
        actions={
          <>
            <Button
              onClick={() => exportCsv.mutate()}
              disabled={exportCsv.isPending || contacts.length === 0}
              title="Exports the rows matching your current filters"
            >
              <Download size={15} />
              {exportCsv.isPending ? "Preparing…" : "Export CSV"}
            </Button>
            {canWrite ? (
              <Link
                to="/customers/contacts/new"
                className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
              >
                <Plus size={16} /> Add {label.replace(/s$/, "").toLowerCase()}
              </Link>
            ) : null}
          </>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative min-w-56 flex-1">
          <Search
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            type="search"
            value={draft}
            onChange={(event) => onDraftChange(event.target.value)}
            placeholder="Filter this list"
            aria-label="Filter by name, email or phone"
            className="w-full rounded-md border-0 bg-white py-2 pl-9 pr-3 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
          />
        </div>

        <div className="flex flex-wrap gap-1">
          {["", ...STAGES].map((value) => (
            <button
              key={value || "all"}
              onClick={() => update({ stage: value, cursor: "" })}
              className={cx(
                "rounded-full px-3 py-1.5 text-xs font-medium capitalize transition",
                stage === value
                  ? "bg-slate-900 text-white"
                  : "bg-white text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50",
              )}
            >
              {value || "All"}
            </button>
          ))}
        </div>

        {filtering || exportCsv.isError ? (
          <Button
            variant="ghost"
            onClick={() => setParams(new URLSearchParams(), { replace: true })}
          >
            <X size={14} /> Clear
          </Button>
        ) : null}
      </div>

      {availableTags.length > 0 ? (
        <div className="mb-4 flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium text-slate-500">Tags:</span>
          {availableTags.map((tag) => {
            const active = selectedTags.includes(tag);
            return (
              <button
                key={tag}
                aria-pressed={active}
                onClick={() => {
                  const next = active
                    ? selectedTags.filter((t) => t !== tag)
                    : [...selectedTags, tag];
                  update({ tags: next.join(","), cursor: "" });
                }}
                className={cx(
                  "rounded-full px-2.5 py-1 text-xs font-medium transition",
                  active
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-600 ring-1 ring-inset ring-slate-300 hover:bg-slate-50",
                )}
              >
                {tag}
              </button>
            );
          })}
          {availableTags.length === 0 && !facets.isPending ? (
            <span className="text-xs text-slate-400">
              No tags yet — add some while editing a record.
            </span>
          ) : null}
        </div>
      ) : null}

      {exportCsv.isError ? (
        <p role="alert" className="mb-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          Export failed. Try again.
        </p>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
        {query.isPending ? (
          <TableSkeleton rows={8} />
        ) : query.isError ? (
          <ErrorState error={query.error} />
        ) : contacts.length === 0 ? (
          <EmptyState
            icon={<Users size={28} />}
            title={filtering ? "Nothing matches that" : `No ${plural(label).toLowerCase()} yet`}
            body={
              filtering
                ? "Try a different name, or clear the filters."
                : "Records you add will appear here."
            }
            action={
              filtering ? (
                <Button onClick={() => setParams(new URLSearchParams(), { replace: true })}>
                  Clear filters
                </Button>
              ) : canWrite ? (
                <Link
                  to="/customers/contacts/new"
                  className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white"
                >
                  <Plus size={15} /> Add {label.replace(/s$/, "").toLowerCase()}
                </Link>
              ) : null
            }
          />
        ) : (
          <>
            {/* Desktop: a real table. */}
            <table className="hidden w-full sm:table">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2.5">Name</th>
                  <th className="px-4 py-2.5">Stage</th>
                  <th className="px-4 py-2.5">Tags</th>
                  <th className="px-4 py-2.5">City</th>
                  <th className="px-4 py-2.5">Outstanding</th>
                  <th className="px-4 py-2.5 text-right">Last contact</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {contacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <Link to={`/customers/contacts/${contact.id}`} className="block max-w-xs">
                        <span className="block truncate text-sm font-medium text-slate-900">
                          {contact.display_name}
                        </span>
                        <span className="block truncate text-xs text-slate-500">
                          {[contact.email, contact.phone].filter(Boolean).join(" · ") || "—"}
                        </span>
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <StageBadge stage={contact.lifecycle_stage} />
                    </td>
                    <td className="px-4 py-3">
                      <TagList contact={contact} />
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{contact.city || "—"}</td>
                    <td className="px-4 py-3 text-sm tabular-nums text-slate-700">
                      {Number(contact.outstanding_balance.amount ?? 0) === 0
                        ? "—"
                        : formatMoney(contact.outstanding_balance)}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-slate-500">
                      {formatRelative(contact.last_contacted_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile: cards. A 6-column table on a phone is not a table. */}
            <ul className="divide-y divide-slate-100 sm:hidden">
              {contacts.map((contact) => (
                <li key={contact.id}>
                  <Link
                    to={`/customers/contacts/${contact.id}`}
                    className="block px-4 py-3 hover:bg-slate-50"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-900">
                          {contact.display_name}
                        </p>
                        <p className="truncate text-xs text-slate-500">
                          {[contact.email, contact.phone].filter(Boolean).join(" · ") || "—"}
                        </p>
                      </div>
                      <StageBadge stage={contact.lifecycle_stage} />
                    </div>
                    <div className="mt-1.5">
                      <TagList contact={contact} />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <nav className="mt-4 flex items-center justify-between">
        <Button onClick={() => update({ cursor: "" })} disabled={!cursor || query.isFetching}>
          <ChevronLeft size={16} /> First
        </Button>
        <span className="text-xs text-slate-500">
          {query.isFetching ? "Loading…" : cursor ? "Next page" : "First page"}
        </span>
        <Button
          onClick={() => nextCursor && update({ cursor: nextCursor })}
          disabled={!nextCursor || query.isFetching}
        >
          Next <ChevronRight size={16} />
        </Button>
      </nav>
    </>
  );
}

function TagList({ contact }: { contact: Contact }) {
  const tags = (contact.tags as string[] | undefined) ?? [];
  if (tags.length === 0) return <span className="text-xs text-slate-300">—</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {tags.slice(0, 2).map((tag) => (
        <span
          key={tag}
          className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
        >
          {tag}
        </span>
      ))}
      {tags.length > 2 ? (
        <span className="text-xs text-slate-400">+{tags.length - 2}</span>
      ) : null}
    </span>
  );
}

function TrashTable({
  contacts,
  pending,
  error,
  onRestore,
  restoring,
}: {
  contacts: Contact[];
  pending: boolean;
  error: unknown;
  onRestore: (id: string) => void;
  restoring: boolean;
}) {
  if (pending) return <TableSkeleton rows={4} />;
  if (error) return <ErrorState error={error} />;
  if (contacts.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white shadow-xs">
        <EmptyState
          icon={<Trash2 size={28} />}
          title="The Bin is empty"
          body="Anything you delete waits here for 30 days before it goes for good."
        />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xs">
      <table className="hidden w-full sm:table">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
            <th className="px-4 py-2.5">Name</th>
            <th className="px-4 py-2.5">Stage</th>
            <th className="px-4 py-2.5 text-right">Action</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {contacts.map((contact) => (
            <tr key={contact.id}>
              <td className="px-4 py-3">
                <p className="text-sm font-medium text-slate-900">{contact.display_name}</p>
                <p className="text-xs text-slate-500">{contact.email || "—"}</p>
              </td>
              <td className="px-4 py-3">
                <StageBadge stage={contact.lifecycle_stage} />
              </td>
              <td className="px-4 py-3 text-right">
                <Button onClick={() => onRestore(contact.id)} disabled={restoring}>
                  <RotateCcw size={14} /> Restore
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <ul className="divide-y divide-slate-100 sm:hidden">
        {contacts.map((contact) => (
          <li key={contact.id} className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-900">
                {contact.display_name}
              </p>
              <p className="truncate text-xs text-slate-500">{contact.email || "—"}</p>
            </div>
            <Button onClick={() => onRestore(contact.id)} disabled={restoring}>
              <RotateCcw size={14} /> Restore
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** `next` is an absolute URL; the cursor is its trailing query parameter. */
function cursorFrom(next: string | null | undefined): string | undefined {
  if (!next) return undefined;
  return new URL(next, window.location.origin).searchParams.get("cursor") ?? undefined;
}

/** Copy is chosen by machine code, never by parsing the message. */
function ErrorState({ error }: { error: unknown }) {
  if (!(error instanceof ApiError)) {
    return <EmptyState title="Something went wrong" body="Please try again." />;
  }
  return (
    <EmptyState
      title={error.isPlanLimit ? "Plan limit reached" : "Could not load contacts"}
      body={error.message}
    />
  );
}